import { ReactElement, useEffect, useState, useRef, useCallback } from "react";
import Select from "react-select"; 
import ReCAPTCHA from 'react-google-recaptcha'
import useApiState, { api } from "../../hooks/use-api-state";
import { IAppInfo, IUserInfo } from "../../messages/IAppInfo";
import Loading from "../common/Loading";
import styles from "./Feedback.module.css";
import "../common/General.module.css";
import { IFeedbackGetResponse, IFeedbackQuestion, IFeedbackAnswers } from "../../messages/IFeedback";


export default function Feedback(props: {
  appInfo: IAppInfo,
  userInfo: IUserInfo,
}): ReactElement {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(props.userInfo?.email ?? null);
  const [fullname, setFullname] = useState<string | null>(props.userInfo?.fullname ?? null);
  const [role, setRole] = useState<string | null>(null);
  const [cohortId, setCohortId] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState<string | null>(null);
  const [formData, setFormData] = useState<IFeedbackGetResponse | null>(null); // So that we don't have to store all individual fields in the state
  const [feedbackApiState, setFeedbackApiState] = useApiState(false);
  const [submitFeedbackApiState, setSubmitFeedbackApiState] = useApiState(false);
  const [recaptchaOK, setRecaptchaOK] = useState(false);
  const recaptcha = useRef<ReCAPTCHA | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [topText, setTopText] = useState<string[] | null>(null);
  const [questions, setQuestions] = useState<IFeedbackQuestion[] | null>(null);

  useEffect(() => {
    document.title = "Training feedback form";
    const searchParams = new URLSearchParams(window.location.search);
    const cohort_id = searchParams.get("id");
    if (cohort_id)
      setCohortId(cohort_id);
  }, [props.userInfo, props.appInfo]);


  const getFeedback = useCallback(async (cohort_id: string) => {
      try {
        let ret = await api.getFeedback(setFeedbackApiState, cohort_id);
        setFormData(ret);
        if (ret.access_token)
          setAccessToken(ret.access_token);
        if (ret.email)
          setEmail(ret.email);
        if (ret.fullname)
          setFullname(ret.fullname);
        if (ret.text) {
          setTopText(Array.isArray(ret.text) ? ret.text : [ret.text]);
        }
        let formTitle = ret.midterm ? ret.form_title_midterm : ret.form_title;
        if (!formTitle)
          formTitle = `${ret.midterm ? "Midterm" : "Training"} feedback form`;
        setFormTitle(formTitle);
        if (ret.message)
          setMessage(ret.message);
        setQuestions(ret.questions.length>0 ? ret.questions : null);
      } catch (e: any) {
        console.error("Error fetching assessment:", e);
      }
  }, [setFeedbackApiState]);

  useEffect(() => {
    if (accessToken)
      setRecaptchaOK(true);
  }, [accessToken]);

  useEffect(() => {
    if (cohortId)
      getFeedback(cohortId);
    else
      setQuestions(null);
  }, [getFeedback, cohortId]);


  function recaptchaChanged(token: string|null) {
      setRecaptchaOK(!!recaptcha.current?.getValue());
  };


  async function submitFeedback() {
      try {
        if (formData) {
          let answers : IFeedbackAnswers = { feedback_form_type: formData.feedback_form_type, midterm: formData.midterm };
          for (const question of questions ?? []) {
            if (question.id && question.answer !== undefined) {
              answers[question.id] = question.answer;
            }
          }
          if (cohortId && questions && answers) {
            const recaptchaValue = !!accessToken ? null : (recaptcha.current?.getValue() ?? null);
            let ret = await api.submitFeedback(setSubmitFeedbackApiState, cohortId, accessToken, fullname, email, role, recaptchaValue, answers);
            if (ret.message) {
              setMessage(ret.message);
              window.scrollTo({ top: 0, left: 0});
              if (ret.status==="OK") {
                setSubmitted(true);
              }
            }
          }
        }
      } catch (e: any) {
        console.error("Error submitting assessment:", e);
      }
  }


  function type2style(type: string): string {
    switch (type) {
      case "label":
        return styles.labelDiv;
      case "text":
        return styles.textDiv;
      case "score6_header":
        return styles.score6HeaderDiv;
      case "score6":
        return styles.score6Div;
      case "checkbox":
        return styles.checkboxDiv;
      case "separator":
        return styles.separatorDiv;
      default:
        return "";
    }
  }

  return (
    <>
      <Loading isLoading={feedbackApiState.isLoading}>
        <div className={styles.feedbackForm}>
          {message && (
            <div className={styles.message}>
              {message}
            </div>
          )}
          {questions && (
            <>
              {formData && (
              <>
                <h2>{formTitle}</h2>
                {formData.title && formData.company && (
                <h3>{formData.title} @ {formData.company}</h3>
                )}
                {formData.trainer && formData.dates && (
                <h4>With {formData.trainer}, {formData.dates}</h4>
                )}
              </>
              )}
              <div className={styles.topTextDiv}>
              Dear {props.userInfo?.fullname ? props.userInfo?.fullname : "Participant"},
              </div>
              {topText && (
              <div className={styles.topTextDiv}>
                { topText.map((text, i) => <div key={i}><p key={i}>{text}</p></div>) }
              </div>
              )}
              <div className={styles.userInfoFields}>
              <div className={styles.userInfoDiv}>
                <label htmlFor="fullname">Full name</label>
                <input 
                id="fullname"
                type="text"
                autoComplete="name"
                value={fullname ?? ""}
                onChange={e => setFullname(e.target.value)}
                className={styles.input}
                placeholder="Enter your name"
                />
              </div>
              <div className={styles.userInfoDiv}>
                <label htmlFor="email">Email</label>
                <input
                id="email"
                type="email"
                autoComplete="email"
                value={email ?? ""}
                onChange={e => setEmail(e.target.value)}
                className={styles.input}
                placeholder="Enter your email"
                />
              </div>
              <div className={styles.userInfoDiv}>
                <label htmlFor="role">Role</label>
                <Select
                  inputId="role"
                  className={"roleSelector"}
                  classNamePrefix={"roleSelector"}
                  options={Object.entries(["Management","Developer","Tester","Architect","Data science","Security"]).map(([key, label], index) => {
                  return { label: label, value: index+1 };
                  })}
                  onChange={option => { setRole(option?.label ?? ""); }}
                  value={{ label: role, value: 0 }}
                  defaultValue={{ label: role, value: 0 }}
                  placeholder="Select your role"
                />
              </div>
              </div>
              {questions.map((question, index) => (
              <div key={index} className={type2style(question.type)}>
                {(() => {
                if (question.midterm === undefined || question.midterm === formData?.midterm) {
                switch (question.type) {
                case "label":
                  return <p className={styles.label} dangerouslySetInnerHTML={{ __html: question.text }} />
                case "text":
                  return (
                  <>
                    {question.text && 
                      <p dangerouslySetInnerHTML={{ __html: question.text }}></p>
                    }
                    <textarea
                    id={question.id}
                    value={typeof question.answer === "string" ? question.answer : ""}
                    placeholder={question.placeholder}
                    onChange={e => {
                      const updated = [...questions];
                      updated[index] = { ...question, answer: e.target.value };
                      setQuestions(updated);
                      // Auto-resize
                      const textarea = e.target as HTMLTextAreaElement;
                      textarea.style.height = "auto";
                      textarea.style.height = textarea.scrollHeight + "px";
                    }}
                    style={{ overflow: "hidden", resize: "none" }}
                    rows={1}
                    />
                  </>
                  );
                case "score6_header":
                  return <>
                    <div className={styles.score6HeaderOptions}>
                    <span>🙁</span>
                    <span>😐</span>
                    <span>🙂</span>
                    </div>
                  </>;
                case "score6":
                  return (
                  <>
                    <div className={styles.score6Text} dangerouslySetInnerHTML={{ __html: question.text }}></div>
                    <div className={styles.score6Fill}>&nbsp;</div>
                    <div className={styles.score6Options}>
                    {[1, 2, 3, 4, 5, 6].map(num => (
                    <label key={num}>
                      <input
                      key={num}
                      className={styles.score6Radio}
                      type="radio"
                      name={`score6_${index}`}
                      value={num}
                      checked={question.answer === num}
                      onChange={() => {
                        const updated = [...questions];
                        updated[index] = { ...question, answer: num };
                        setQuestions(updated);
                      }}
                      />
                      {num}
                    </label>
                    ))}
                    </div>
                  </>
                  );
                case "checkbox":
                  return (
                    <>
                    <p dangerouslySetInnerHTML={{ __html: question.text }} style={{ display: "inline", marginRight: "8px" }} />
                    <input
                      id={question.id}
                      type="checkbox"
                      checked={!!question.answer}
                      onChange={e => {
                      const updated = [...questions];
                      updated[index] = { ...question, answer: e.target.checked };
                      setQuestions(updated);
                      }}
                      style={{ marginLeft: "8px" }}
                    />
                    </>
                  );
                case "separator":
                  return (
                    <div
                    style={{
                      marginTop: `${question.margin_top ?? "10"}px`,
                      marginBottom: `${question.margin_bottom ?? "10"}px`,
                      borderBottom: question.line ?? ``,
                    }}
                    >&nbsp;</div>
                  );
                default:
                  return null;
                }
                }
                })()}
              </div>
              ))}

              {!submitted && (
              <>
                {!accessToken && (
                  <div className={styles.bottomRecaptchaDiv}>
                  <div className={styles.recaptcha}>
                    <ReCAPTCHA ref={recaptcha} sitekey={props.appInfo.captcha_sitekey} onChange={recaptchaChanged}/>
                  </div>
                  </div>
                )}

                <Loading isLoading={submitFeedbackApiState.isLoading}>
                <button
                  className={styles.submitButton}
                  onClick={submitFeedback}
                  disabled={!recaptchaOK}
                >
                  Submit
                </button>
                </Loading>
              </>
              )}
            </>
          )}
        </div>
      </Loading>
    </>
  );
}
